/**
 * Arredonda uma string de tempo (HH:mm) para a meia hora mais próxima
 * Exemplos:
 * - 10:20 → 10:30
 * - 10:50 → 11:00
 * - 11:00 → 11:00
 * - 11:15 → 11:30
 */
export function roundToNearestHalfHour(timeString) {
  if (!timeString) return '09:00';
  
  const [hours, minutes] = timeString.split(':').map(Number);
  const totalMinutes = hours * 60 + minutes;
  
  // Arredondar para a meia hora mais próxima
  const roundedMinutes = Math.round(totalMinutes / 30) * 30;
  
  const roundedHours = Math.floor(roundedMinutes / 60);
  const roundedMins = roundedMinutes % 60;
  
  return `${String(roundedHours).padStart(2, '0')}:${String(roundedMins).padStart(2, '0')}`;
}

/**
 * Adiciona 1 hora a uma string de tempo (HH:mm)
 * Exemplo: 10:30 → 11:30
 */
export function addOneHour(timeString) {
  if (!timeString) return '10:00';
  
  const [hours, minutes] = timeString.split(':').map(Number);
  const newHours = (hours + 1) % 24;
  
  return `${String(newHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/**
 * Arredonda o tempo e define automaticamente o fim como início + 1 hora
 * Retorna { startTime: string, endTime: string }
 */
export function roundAndSetDuration(startTime) {
  const roundedStart = roundToNearestHalfHour(startTime);
  const endTime = addOneHour(roundedStart);
  
  return {
    startTime: roundedStart,
    endTime: endTime
  };
}

/**
 * Encontra o próximo slot disponível baseado no tempo atual
 * Se for 9:20 e o slot é 9:30-10:30, retorna 9:30-10:30
 * Verifica conflitos com tarefas existentes do mesmo utilizador no mesmo dia
 */
export function findNextAvailableSlot(currentTime, existingTasks = [], assignedTo, date) {
  // Converter tempo atual para minutos
  const [currentHours, currentMinutes] = currentTime.split(':').map(Number);
  const currentTotalMinutes = currentHours * 60 + currentMinutes;
  
  // Calcular o próximo slot de 30 minutos
  // Se for 9:20, queremos 9:30
  // Se for 9:30, queremos 9:30 (ainda estamos nesse slot)
  // Se for 9:31, queremos 10:00
  let nextSlotMinutes;
  if (currentMinutes % 30 === 0) {
    // Já está no slot de 30 minutos (ex: 9:00, 9:30), usar esse
    nextSlotMinutes = currentTotalMinutes;
  } else {
    // Arredondar para o próximo slot de 30 minutos
    nextSlotMinutes = Math.ceil(currentTotalMinutes / 30) * 30;
  }
  
  // Verificar conflitos com tarefas existentes do mesmo utilizador no mesmo dia
  const tasksOnDate = existingTasks.filter(task => {
    if (task.assignedTo !== assignedTo || task.date !== date) return false;
    if (!task.startTime || !task.endTime) return false;
    return true;
  });
  
  // Função para verificar sobreposição de horários
  const timeToMinutes = (timeStr) => {
    const [h, m] = timeStr.split(':').map(Number);
    return h * 60 + m;
  };
  
  const hasConflict = (newStart, newEnd) => {
    const newStartMin = timeToMinutes(newStart);
    const newEndMin = timeToMinutes(newEnd);
    
    return tasksOnDate.some(task => {
      const taskStartMin = timeToMinutes(task.startTime);
      const taskEndMin = timeToMinutes(task.endTime);
      
      // Verificar sobreposição: nova tarefa começa antes do fim da existente
      // e termina depois do início da existente
      return !(newEndMin <= taskStartMin || newStartMin >= taskEndMin);
    });
  };
  
  // Tentar até encontrar um slot sem conflito (máximo 20 tentativas = 10 horas à frente)
  let attempts = 0;
  let startTime;
  let endTime;
  
  while (attempts < 20) {
    const slotHours = Math.floor(nextSlotMinutes / 60) % 24;
    const slotMins = nextSlotMinutes % 60;
    startTime = `${String(slotHours).padStart(2, '0')}:${String(slotMins).padStart(2, '0')}`;
    endTime = addOneHour(startTime);
    
    if (!hasConflict(startTime, endTime)) {
      break;
    }
    
    // Avançar para o próximo slot de 30 minutos
    nextSlotMinutes += 30;
    attempts++;
  }
  
  return { startTime, endTime };
}



